<script setup lang="ts">
import { refDebounced, useEventListener } from "@vueuse/core";
import { computed, defineAsyncComponent, inject, nextTick, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import Icon from "@/design/primitives/Icon.vue";
import { useCan } from "@/features/auth/api/can";
import { usePipelines } from "@/features/board/api/use-board";
import { useChannels } from "@/features/channels/api/use-channels";
import { useComponentScreens } from "@/features/component-views/api/use-component-view";
import { useBilledByHq } from "@/features/organization/api/use-organization";
import { usePlatformAccess } from "@/features/platform/api/use-platform";
import { useSearch, useSystemPieces } from "@/features/search/api/use-search";
import { useArchivedStructure, useStructure } from "@/features/structure/api/use-structure";
import { useMyAccess } from "@/shared/access/use-access";
import { LOOKUP_PORT } from "@/shared/contracts/ports";
import { formatPhone } from "@/shared/format/phone";
import { pieceKey } from "@/shared/system-piece";
import { paletteScopeMessages } from "./i18n/palette";
import { paletteDestinations } from "./nav-items";
import PaletteResults, {
	type BadgeTone,
	type PaletteRow,
	type PaletteSection,
} from "./PaletteResults.vue";
import { paletteActions, type QuickCreateKind } from "./palette-actions";
import { drawDestinations, drawPieces, matches, rankRows } from "./palette-search";

/**
 * Sob demanda, como as páginas no `router.ts` e pelo mesmo motivo: esta paleta
 * vive na moldura de TODA tela, e os quatro formulários que ela pode abrir são o
 * criador de funil, o de agente, o de contato e o de conversa. Quem nunca aperta
 * ⌘K não paga por nenhum deles.
 */
const QuickCreate = defineAsyncComponent(() => import("./QuickCreate.vue"));

/**
 * ⌘K, and the main route for anyone who uses the product daily.
 *
 * In a system where tenants create their own structures the menu inevitably
 * grows; the palette is what keeps that growth from becoming the problem (F-R8).
 *
 * Este arquivo ficou com TRÊS coisas e nada mais: o overlay, o teclado e o cursor.
 * Quem desenha as seções é o `PaletteResults`, quem nomeia os comandos de criação
 * é o `palette-actions`, e quem sabe quais telas existem é o `nav-items` — que é a
 * mesma lista que o menu lê, e é por isso que as duas não podem mais divergir.
 *
 * ── AS TRÊS ORIGENS DE UMA LINHA ──────────────────────────────────────────
 *
 * DESTINO (um lugar, cortado em `LIMIT` POR SEÇÃO), RESULTADO (o que o servidor
 * achou nos contatos, nas conversas e nos registros) e AÇÃO (criar sem sair
 * daqui). As três entram numa lista só e NUMERADA UMA VEZ: a seta anda sobre
 * `rows`, e um segundo contador por seção quebraria o teclado que já funcionava.
 *
 * ── OS QUATRO DESTINOS, E POR QUE OS DOIS ÚLTIMOS FALTAVAM ────────────────
 *
 * As telas do produto, as TELAS que este workspace desenhou (`/t/`), os FUNIS
 * (`/board?funil=`) e o cadastro cru de cada entidade (`/e/`). Os dois do meio já
 * estavam no menu e no agente e não estavam aqui: quem trabalha numa tela do
 * construtor ou num funil chegava lá pela lateral e nunca pelo ⌘K, que é a porta
 * de quem sabe o nome do lugar. Nenhum dos dois custa rede: o menu já leu as duas
 * listas, e o cache é o mesmo.
 */
const props = defineProps<{ open: boolean }>();
const emit = defineEmits<{ close: [] }>();
const router = useRouter();

const { t, te } = useI18n({
	useScope: "local",
	inheritLocale: true,
	messages: paletteScopeMessages,
});

/** A chave traduzida, ou nada — é o que deixa a tabela de outro lote cair no texto dela. */
const say = (key: string): string | undefined => (te(key) ? t(key) : undefined);

const { data: entities } = useStructure();
const { data: platformAdmin } = usePlatformAccess();
const { data: channels } = useChannels();
const { data: screens } = useComponentScreens();
const { data: pipelines } = usePipelines();
const isAdmin = useCan("admin");
const { data: moduleAccess } = useMyAccess();
/** A Cobrança não se oferece num workspace que a matriz paga — a mesma regra do menu. */
const { billedByHq } = useBilledByHq(isAdmin);
/** As peças de "Seu sistema": só para quem pode abri-las, e só com a paleta aberta. */
const { data: pieces } = useSystemPieces(
	() => props.open && isAdmin.value && !moduleAccess.value?.disabledModules?.includes("system"),
);

/** As arquivadas também têm nome: o funil ou a automação que ficou sobre uma não pode mostrar o código. */
const { data: archivedEntities } = useArchivedStructure(() => props.open);

/** O nome de uma tabela de dados, para nenhuma segunda linha mostrar o código dela. */
function entityName(code: string): string {
	const live = entities.value?.find((e) => e.code === code);
	if (live) return live.name;
	const gone = archivedEntities.value?.find((e) => e.code === code);
	return gone ? t("palette.piece.archived", { name: gone.name }) : code;
}

const term = ref("");
const cursor = ref(0);
/** Qual diálogo de criação está aberto POR CIMA da tela atual, ou nenhum. */
const creating = ref<QuickCreateKind | null>(null);

/**
 * As telas, vindas da lista única.
 *
 * O array à mão que morava aqui já divergia do menu em cinco telas e prendia
 * "Consumo" atrás do administrador da instalação — que é a conta do próprio
 * tenant. A paleta esconde o que o menu esconde porque lê o MESMO filtro de
 * papel: uma segunda porta para toda tela não pode ser uma porta sem tranca.
 *
 * `paletteDestinations` e não `visibleItems`: além das telas, ela devolve cada
 * ABA de `/settings` pelo nome dela. Quatro destinos da lateral viraram abas, e
 * uma página única que não pudesse ser procurada por "senha" ou "fuso horário"
 * teria trocado quatro lugares óbvios por um esconderijo.
 */
const destinations = computed<PaletteRow[]>(() => [
	...drawDestinations(
		paletteDestinations({
			isAdmin: isAdmin.value,
			disabledModules: moduleAccess.value?.disabledModules,
			platformAdmin: platformAdmin.value === true,
			billedByHq: billedByHq.value,
		}),
		say,
	).map((item) => ({
		key: item.to,
		label: item.label,
		hint: item.hint,
		terms: item.terms,
		to: item.to,
		section: (item.platformAdmin ? "admin" : "product") as PaletteSection,
	})),
	/**
	 * AS TELAS QUE ESTE WORKSPACE DESENHOU, cada uma no endereço dela.
	 *
	 * Só as publicadas chegam aqui — o servidor filtra por versão ativa —, então a
	 * paleta não oferece rascunho, exatamente como o menu não oferece. `terms` leva
	 * os dois códigos porque quem construiu a tela procura por eles antes de
	 * procurar pelo nome bonito que deu a ela.
	 */
	...(screens.value ?? []).map((screen) => ({
		key: `/t/${screen.entityCode}/${screen.code}`,
		label: screen.name,
		hint: screen.entityName,
		terms: `${screen.entityCode} ${screen.code} ${t("palette.terms.screen")}`,
		to: `/t/${screen.entityCode}/${screen.code}`,
		section: "screens" as PaletteSection,
	})),
	/**
	 * OS FUNIS, um por linha — e SEM o teto de oito que a lateral tem.
	 *
	 * O teto do menu existe porque a lateral é uma coluna e trinta linhas nela
	 * empurram todo o resto para fora da tela. A paleta é uma peneira: ela já corta
	 * em `LIMIT` e já escreve o denominador ("8 de 30"), então repetir o teto aqui
	 * seria esconder vinte e dois funis sem nem dizer que existem.
	 *
	 * `GET /v3/board/pipelines` já responde só os funis de que a pessoa é membro,
	 * que é a mesma tranca do quadro — a paleta não é uma segunda porta sem ela.
	 */
	...(pipelines.value ?? []).map((pipeline) => ({
		key: `/board?funil=${pipeline.code}`,
		label: pipeline.name,
		// O nome da tabela de dados, nunca o código dela.
		hint: entityName(pipeline.entityCode),
		terms: `${pipeline.code} ${pipeline.entityCode} ${t("palette.terms.funnel")}`,
		to: `/board?funil=${pipeline.code}`,
		section: "funnels" as PaletteSection,
	})),
	// As entidades continuam saindo da estrutura viva, nunca de um array: a
	// plataforma não conhece nenhuma delas pelo nome.
	...(entities.value ?? [])
		.filter((e) => e.status === "active")
		.map((e) => ({
			key: `/e/${e.code}`,
			label: e.name,
			// A seção já diz o que é; o código vai só para a peneira.
			hint: "",
			terms: e.code,
			to: `/e/${e.code}`,
			section: "entities" as PaletteSection,
		})),
	// Só com algo digitado: aberta vazia, a paleta oferece lugares, não o inventário inteiro.
	// A tabela arquivada não é peça do mapa; acha-se pelo nome e abre a lista das arquivadas.
	...(term.value.trim()
		? (archivedEntities.value ?? []).map((e) => ({
				key: `archived:${e.code}`,
				label: e.name,
				hint: t("palette.piece.archived", { name: t("palette.piece.entity") }),
				terms: e.code,
				to: "/system?no=archived",
				section: "pieces" as PaletteSection,
			}))
		: []),
	...(term.value.trim()
		? drawPieces(
				pieces.value ?? [],
				entityName,
				(key, params) => t(key, params ?? {}),
				offeredPieces.value,
			).map((row) => ({ ...row, section: "pieces" as PaletteSection }))
		: []),
]);

/** As peças que já têm linha própria acima — "Seu sistema" não as repete. */
const offeredPieces = computed(
	() =>
		new Set([
			...(entities.value ?? [])
				.filter((e) => e.status === "active")
				.map((e) => pieceKey.entity(e.code)),
			...(pipelines.value ?? []).map((p) => pieceKey.pipeline(p.code)),
			...(screens.value ?? []).map((v) => pieceKey.view(v.entityCode, v.code)),
		]),
);

/**
 * Quantos cabem na vista sem virar rolagem cega — POR SEÇÃO.
 *
 * Era um corte só sobre a lista inteira de destinos, e com telas e funis dentro
 * dela isso teria virado um defeito silencioso: oito telas do produto engoliriam
 * as oito primeiras vagas e nenhuma tela do tenant apareceria. Cada seção corta a
 * sua, e o denominador de cada uma continua sendo escrito ao lado.
 */
const LIMIT = 8;

/**
 * Tudo que serve à busca — o denominador do "8 de 23", que o corte escondia.
 *
 * `terms` entra na peneira e não na tela: é onde moram os sinônimos das abas de
 * Configurações ("senha", "fuso horário", "tema", "convite"), que precisam achar
 * a aba sem caber na linha estreita em que o `hint` é desenhado.
 *
 * A comparação dobra os acentos (`matches`) porque os `terms` estão escritos sem
 * eles e os rótulos com eles: sem dobrar, "saúde" não achava a aba Sistema e
 * "configuracoes" não achava coisa nenhuma.
 */
const foundDestinations = computed(() => destinations.value.filter((d) => matches(d, term.value)));

/**
 * As seções de DESTINO, na ordem em que a lista as desenha.
 *
 * Elas e só elas são cortadas em `LIMIT` e contadas antes do corte — o que vem do
 * servidor já chega cortado de lá, com o total ao lado.
 */
const DESTINATION_SECTIONS: readonly PaletteSection[] = [
	"product",
	"screens",
	"funnels",
	"entities",
	"pieces",
	"admin",
];

/** As seções abertas por "ver mais" — voltam ao corte a cada nova busca. */
const expanded = ref(new Set<PaletteSection>());
watch(term, () => {
	expanded.value = new Set();
});

const cutDestinations = computed<PaletteRow[]>(() =>
	DESTINATION_SECTIONS.flatMap((section) => {
		// Ranqueia antes do corte: o nome exato não pode ficar atrás do oitavo.
		const found = rankRows(
			foundDestinations.value.filter((d) => d.section === section),
			term.value,
		);
		return expanded.value.has(section) ? found : found.slice(0, LIMIT);
	}),
);

/**
 * O que o servidor achou. UMA chamada, não uma por entidade ativa — que é um
 * número que o tenant escolhe. O `to` já vem pronto de lá.
 */
const debounced = refDebounced(term, 250);
const { data: found, isFetching, asked } = useSearch(debounced);

/**
 * O NOME de um registro é o mesmo da tela inteira (`composedName`, pela porta de
 * busca): o servidor rotula pelo primeiro texto que acha — uma modalidade
 * ("Particular") ou um pedaço do id —, e a lista e o seletor já chamavam a mesma
 * linha de "Jõao · 19/10/2026 08:00". Pedido por linha, e a porta guarda em cache.
 */
const lookup = inject(LOOKUP_PORT, null);
const recordNames = ref<Record<string, string>>({});
watch(found, (answer) => {
	for (const hit of answer?.items ?? []) {
		const key = `${hit.entityCode}:${hit.id}`;
		if (hit.kind !== "record" || !hit.entityCode || recordNames.value[key]) continue;
		lookup
			?.label({ entity: hit.entityCode, id: hit.id })
			.then((name) => {
				if (name && name !== hit.id) recordNames.value[key] = name;
			})
			.catch(() => undefined);
	}
});

/**
 * O NOME da etapa em que um registro está, a partir do código que veio no
 * resultado.
 *
 * A decisão de 23/08 pôs a etapa NO REGISTRO, então o servidor devolve o código
 * dela sem consulta a mais. Quem sabe traduzir "prop_env" em "Proposta enviada" é
 * quem tem os funis carregados — e esta tela acabou de carregá-los para oferecê-los
 * como destino. Pedir o rótulo ao servidor faria ele ler o funil de cada entidade
 * a cada tecla, para escrever o que já está aqui.
 */
function stageName(entityCode: string | null | undefined, stage: string): string {
	for (const pipeline of pipelines.value ?? []) {
		if (entityCode && pipeline.entityCode !== entityCode) continue;
		const match = pipeline.stages.find((s) => s.code === stage);
		if (match) return match.name;
	}
	return stage;
}

/**
 * A cor do chip da etapa, estável por etapa.
 *
 * A V2 tinha onze cores fixas para status genérico, mapeadas por nome. Aqui não há
 * nome conhecido — a etapa é do tenant —, então a cor sai de um hash do código: a
 * mesma etapa tem sempre a mesma cor, etapas diferentes têm cores diferentes, e
 * nenhuma delas é um hexadecimal autorado que o tema escuro não saberia inverter.
 */
const STAGE_TONES: readonly BadgeTone[] = ["accent", "info", "success", "warning", "neutral"];

function stageTone(code: string): BadgeTone {
	let hash = 5381;
	for (let i = 0; i < code.length; i++) hash = (hash * 33 + code.charCodeAt(i)) % 100_000;
	return STAGE_TONES[hash % STAGE_TONES.length] ?? "neutral";
}

const results = computed<PaletteRow[]>(() =>
	asked.value
		? (found.value?.items ?? []).map((hit) => ({
				key: `${hit.kind}:${hit.id}`,
				label: recordNames.value[`${hit.entityCode}:${hit.id}`] ?? hit.label,
				// O telefone de um contato como se lê; qualquer outra dica passa como veio.
				hint: hit.hint?.startsWith("+") ? (formatPhone(hit.hint) ?? hit.hint) : (hit.hint ?? ""),
				to: hit.to,
				section: hit.kind as PaletteSection,
				// A PESSOA ganha rosto, e só ela: numa lista de dez linhas iguais a
				// inicial com a cor do id é o que se acha antes de ler. A conversa
				// continua com o traço do canal — o rosto ali seria o da mesma pessoa
				// em três threads, o que separa menos do que separaria.
				...(hit.kind === "contact" ? { avatar: { name: hit.label, seed: hit.id } } : {}),
				...(hit.stage
					? {
							badge: {
								text: stageName(hit.entityCode, hit.stage),
								tone: stageTone(hit.stage),
							},
						}
					: {}),
			}))
		: [],
);

/**
 * Quando o servidor não olhou o workspace inteiro, ele diz — e a paleta repete.
 *
 * Uma busca que pulou metade dos cadastros em silêncio é a que faz alguém concluir
 * que o registro não existe.
 */
const note = computed(() => {
	const counted = found.value?.entities;
	if (!counted || counted.searched >= counted.active) return "";
	return t("palette.partial", { searched: counted.searched, active: counted.active });
});

const actions = computed<PaletteRow[]>(() =>
	paletteActions(
		{
			admin: isAdmin.value && !moduleAccess.value?.disabledModules?.includes("agents"),
			hasSendableChannel: (channels.value ?? []).some((c) => c.canSend),
		},
		(kind) => {
			// Fecha a paleta ANTES de abrir o diálogo: o que fica à mostra atrás dele
			// é a tela em que a pessoa estava, que é o ponto inteiro do comando.
			emit("close");
			creating.value = kind;
		},
	)
		// A mesma peneira dos destinos, e DEPOIS de traduzir pelo mesmo motivo que
		// lá: "+ contato" e "+ contact" são a mesma ação, e a chave não é nenhuma
		// das duas. Acento também não decide — a peneira dobra os dois lados.
		.map((action) => ({
			key: `+${action.kind}`,
			label: t(action.labelKey),
			hint: t(action.hintKey),
			section: "action" as PaletteSection,
			run: action.run,
		}))
		.filter((action) => matches(action, term.value)),
);

// O nome que casa decide a ordem das seções: a tabela "Unidades" antes dos ajustes.
const rows = computed<PaletteRow[]>(() =>
	rankRows([...cutDestinations.value, ...results.value, ...actions.value], term.value),
);

/**
 * O denominador de cada seção.
 *
 * Nos destinos é quantos CASAM antes do corte; nos resultados é quantos casam no
 * banco, que o servidor conta e mandava embora — a paleta escrevia "8 de 8" com
 * duzentos contatos casando, que é a linha que faz alguém parar de procurar.
 */
const totals = computed<Partial<Record<PaletteSection, number>>>(() => {
	const counted: Partial<Record<PaletteSection, number>> = {};
	for (const section of DESTINATION_SECTIONS) {
		counted[section] = foundDestinations.value.filter((d) => d.section === section).length;
	}
	const fromServer = found.value?.totals;
	if (fromServer) {
		counted.contact = fromServer.contact;
		counted.conversation = fromServer.conversation;
		counted.record = fromServer.record;
	}
	return counted;
});

/** O que o rodapé mostra à direita: quanto a última resposta custou. */
const tookMs = computed(() => (asked.value ? (found.value?.tookMs ?? null) : null));

/**
 * As saídas oferecidas quando nada casou.
 *
 * A V2 desenhava chips de sugestão vindos de um dicionário de sinônimos em
 * português no servidor. Um dicionário assim divergiria dos sete idiomas deste
 * painel na primeira palavra, e a V3 já tem o mecanismo certo para sinônimo: os
 * `terms` traduzidos. O que sobra do chip — o próximo clique de quem digitou
 * errado — é dado aqui pelos lugares que existem, dois por seção, começando pelo
 * que o tenant construiu.
 */
const SUGGESTION_PER_SECTION = 2;
const SUGGESTION_LIMIT = 6;

const suggestions = computed<PaletteRow[]>(() =>
	DESTINATION_SECTIONS.flatMap((section) =>
		destinations.value.filter((d) => d.section === section).slice(0, SUGGESTION_PER_SECTION),
	).slice(0, SUGGESTION_LIMIT),
);

watch(rows, () => {
	cursor.value = 0;
});

/**
 * O escolhido dentro da vista.
 *
 * A `<ul>` rola, e com a lista maior a seta saía por baixo sem nada acompanhar.
 * `block: "nearest"` porque só quem está fora precisa de rolagem — centralizar
 * faria a lista pular a cada tecla.
 */
const list = ref<HTMLElement | null>(null);
const input = ref<HTMLInputElement | null>(null);

/** O ponteiro também move o cursor, e rolar debaixo dele faria a lista fugir do
 *  mouse. Só a tecla pede a vista; a seta é que anda às cegas. */
let byPointer = false;

watch(cursor, async (index) => {
	if (byPointer) return;
	await nextTick();
	list.value?.querySelector(`[data-index="${index}"]`)?.scrollIntoView({ block: "nearest" });
});

watch(
	() => props.open,
	async (open) => {
		if (!open) return;
		term.value = "";
		cursor.value = 0;
		/**
		 * O foco, PEDIDO — e não um `autofocus` no atributo.
		 *
		 * `autofocus` é honrado uma vez, quando o elemento entra no documento pela
		 * primeira vez. A paleta abre e fecha a tarde inteira sem recarregar a
		 * página, e da segunda vez em diante o campo aparecia sem foco: a pessoa
		 * digitava contra a tela que estava por baixo.
		 */
		await nextTick();
		input.value?.focus();
	},
);

function choose(row: PaletteRow | undefined): void {
	if (!row) return;
	// Uma ação acontece SOBRE a tela atual e ela mesma fecha a paleta; um destino
	// navega. Nenhuma linha faz as duas coisas.
	if (row.run) {
		row.run();
		return;
	}
	if (!row.to) return;
	void router.push(row.to);
	emit("close");
}

/** Um destaque só: o `hover:` do CSS pintava um item enquanto a seta pintava outro. */
function hover(index: number): void {
	byPointer = true;
	cursor.value = index;
}

/**
 * O TECLADO NA JANELA, e não no campo.
 *
 * Estava num `@keydown` do `<input>`, e por isso morria assim que o foco saía
 * dele: clicar num item e apertar `Esc` não fechava nada, e a seta parava de
 * andar. A V2 escutava em `window` exatamente por isso. Guardado por `open`
 * porque a paleta fechada não pode confiscar `Esc` da tela que está embaixo — e
 * o ⌘K continua sendo do `App.vue`, que é o único lugar que abre e fecha.
 */
/** A tecla desenhada, quatro vezes no rodapé — uma constante em vez de quatro cópias. */
const KEY_CLASS =
	"rounded border border-border bg-bg-muted px-1 py-0.5 font-mono text-3xs leading-none text-fg-muted";

useEventListener(window, "keydown", (event: KeyboardEvent) => {
	if (!props.open) return;
	byPointer = false;
	const total = rows.value.length;
	if (event.key === "ArrowDown" && total > 0) {
		event.preventDefault();
		// Circular, como na V2: a última linha leva de volta à primeira. Parar na
		// ponta faz a pessoa achar que a tecla travou.
		cursor.value = (cursor.value + 1) % total;
	}
	if (event.key === "ArrowUp" && total > 0) {
		event.preventDefault();
		cursor.value = (cursor.value - 1 + total) % total;
	}
	if (event.key === "Enter") {
		event.preventDefault();
		choose(rows.value[cursor.value]);
	}
	if (event.key === "Escape") {
		event.preventDefault();
		emit("close");
	}
});
</script>

<template>
	<!--
		TELEPORT para o `body`, como a V2.
		A paleta é filha da moldura, e a moldura tem `transform`, `overflow` e uma
		pilha de `z-index` própria — três coisas que fazem um `fixed` deixar de ser
		relativo à janela. Fora da árvore da casca ela não pode ser recortada por
		nada que a casca decida no futuro.
	-->
	<Teleport to="body">
		<Transition
			enter-active-class="transition-opacity duration-200"
			leave-active-class="transition-opacity duration-150"
			enter-from-class="opacity-0"
			leave-to-class="opacity-0"
		>
			<div
				v-if="open"
				class="fixed inset-0 z-50 flex items-start justify-center bg-black/30 px-4 pt-[15vh] backdrop-blur-sm"
				@click.self="emit('close')"
			>
				<!--
					`max-w-2xl` (672px), a largura da V2, e não os 448px de antes: com a
					segunda linha desenhada por baixo do rótulo — telefone, canal · trecho,
					entidade — 448px cortavam a metade do que a linha tinha a dizer.
				-->
				<div
					class="w-full max-w-2xl overflow-hidden rounded-2xl border border-border bg-surface-raised shadow-lg"
				>
					<!--
						A LINHA DE BUSCA, na medida da V2: o respiro (`py-3`) é da LINHA e não
						do campo, a lupa tem o tamanho do texto que está ao lado dela, e o
						texto é o do corpo (16px) e não o miúdo das tabelas. Um campo que é o
						assunto inteiro do diálogo não se escreve em `text-sm` — e 16px é
						também o que impede o navegador do telefone de dar zoom ao focar.
					-->
					<div class="flex items-center gap-3 px-4 py-3.5">
						<Icon name="search" class="size-5 shrink-0 text-fg-subtle" />
						<input
							ref="input"
							v-model="term"
							:placeholder="t('palette.placeholder')"
							class="palette-input min-w-0 flex-1 bg-transparent text-base text-fg placeholder:text-fg-subtle"
						/>
						<button
							type="button"
							:aria-label="t('palette.footer.close')"
							class="flex size-8 shrink-0 items-center justify-center rounded-full text-fg-subtle transition-colors hover:bg-bg-muted hover:text-fg"
							@click="emit('close')"
						>
							<Icon name="close" class="size-4" />
						</button>
					</div>
					<ul ref="list" class="max-h-[26rem] overflow-y-auto px-2 py-1">
						<PaletteResults
							:rows="rows"
							:totals="totals"
							:cursor="cursor"
							:term="term"
							:searching="isFetching"
							:note="note"
							:suggestions="suggestions"
							@choose="choose"
							@hover="hover"
							@more="expanded = new Set([...expanded, $event])"
						/>
					</ul>

					<!-- As teclas que o `onKey` já trata e que ninguém contava, e o tempo
					     que a resposta custou — os dois cantos do rodapé da V2. -->
					<div
						class="flex items-center justify-between gap-3 bg-bg-muted/50 px-4 py-2 text-xs text-fg-subtle"
					>
						<div class="flex min-w-0 items-center gap-3">
							<span class="flex items-center gap-1">
								<kbd :class="KEY_CLASS">↑</kbd><kbd :class="KEY_CLASS">↓</kbd>
								{{ t("palette.footer.move") }}
							</span>
							<span class="flex items-center gap-1">
								<kbd :class="KEY_CLASS">↵</kbd>
								{{ t("palette.footer.open") }}
							</span>
							<span class="hidden items-center gap-1 sm:flex">
								<kbd :class="KEY_CLASS">esc</kbd>
								{{ t("palette.footer.close") }}
							</span>
						</div>
						<span v-if="tookMs !== null" class="shrink-0 font-mono text-3xs">
							{{ t("palette.footer.took", { ms: tookMs }) }}
						</span>
					</div>
				</div>
			</div>
		</Transition>
	</Teleport>

	<!--
		IRMÃO do overlay, e não filho: o overlay é `v-if="open"`, e o diálogo de
		criação tem de continuar de pé depois que a paleta fecha — é esse fechamento
		que revela a tela por baixo, que é onde a pessoa continua.
	-->
	<QuickCreate v-if="creating" :kind="creating" @close="creating = null" />
</template>

<style scoped>
/**
 * O FOCO CALADO — e por que ele precisa de CSS de verdade.
 *
 * `tokens.css` desenha `*:focus-visible { outline: 2px solid accent }` como
 * padrão do painel — hoje dentro de `@layer base`, onde uma utilitária consegue
 * dispensá-lo. Este campo não escreve `focus:outline-none` na classe: ele pede o
 * silêncio aqui, em CSS de verdade, e continua a ser atendido porque `<style
 * scoped>` sai sem camada nenhuma e estilo sem camada vence camada.
 *
 * Tirar o anel deste campo não tira sinal de ninguém — é a regra geral com uma
 * exceção pedida, não a regra apagada. Este `<input>` é o ÚNICO conteúdo focável
 * de um diálogo modal que ganha o foco sozinho ao abrir: quem chegou pelo teclado
 * já sabe onde está porque a paleta acabou de aparecer por cima de tudo, e o que
 * anuncia o campo é a moldura dele — a linha de baixo, a lupa e o texto de
 * exemplo. É o mesmo silêncio da V2, que escrevia `outline-none` no mesmo lugar.
 *
 * Escopado, e por isso mais específico que o `*`: nenhum outro campo do painel
 * perde o anel por causa desta linha.
 */
.palette-input:focus,
.palette-input:focus-visible {
	outline: none;
}
</style>
